import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

export const useUnreadCount = () => {
  const { user, notificationPreferences } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lastChatVisit, setLastChatVisit] = useState(null);
  const userId = user?.id;
  const chatMessagesEnabled = notificationPreferences?.chatMessages;

  const fetchUnreadCount = useCallback(async () => {
    if (!userId) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      if (notificationPreferences?.chatMessages === false) {
        setUnreadCount(0);
        setLoading(false);
        return;
      }
      
      // Fetch all chats where the user is a participant
      const response = await api.get('/api/chat');
      
      // Use same data structure as Chat component: conversations || chats
      const conversations = response.data.conversations || response.data.chats || [];
      
      if (conversations.length > 0) {
        // Use backend's unreadCount which is based on is_read field in database
        // This ensures consistency across all sessions and devices
        let totalUnread = 0;
        
        for (const conversation of conversations) {
          try {
            // Use backend's unreadCount from database (based on is_read field)
            const chatUnreadCount = conversation.unreadCount || 0;
            totalUnread += chatUnreadCount;
          } catch (error) {
            console.error(`Failed to process conversation ${conversation.id}:`, error);
          }
        }
        
        setUnreadCount(totalUnread);
      } else {
        setUnreadCount(0);
      }
      
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [userId, chatMessagesEnabled]); // Memoize with user.id, not entire user object

  // Fetch unread count on mount and when user.id changes (not entire user object)
  useEffect(() => {
    fetchUnreadCount();
  }, [fetchUnreadCount]); // Now depends on memoized function

  // Refresh unread count every 30 seconds for real-time updates
  useEffect(() => {
    if (!userId) return; // Don't poll if no user
    const interval = setInterval(fetchUnreadCount, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [userId, chatMessagesEnabled]); // Only depend on user.id, not entire user object

  // Listen for markAllMessagesAsRead event
  useEffect(() => {
    if (!userId) return; // Don't set up listeners if no user
    
    const handleMarkAllAsRead = () => {
      // Mark all chats as read by updating localStorage timestamps
      const now = new Date().toISOString();
      
      // We need to get the current chats to update their timestamps
      api.get('/api/chat').then(response => {
        if (response.data.chats) {
          response.data.chats.forEach(chat => {
            const lastReadKey = `lastRead_${chat.id}_${userId}`;
            localStorage.setItem(lastReadKey, now);
          });
          
          // Refresh unread count
          fetchUnreadCount();
        }
      }).catch(err => console.error('Error marking all as read:', err));
    };

    const handleChatMarkedAsRead = (event) => {
      // Refresh unread count when a specific chat is marked as read
      fetchUnreadCount();
    };

    const handleUpdateUnreadCount = (event) => {
      // Refresh unread count when requested
      fetchUnreadCount();
    };

    const handleMessageSent = (event) => {
      // Refresh unread count when a message is sent (new message might arrive)
      setTimeout(() => fetchUnreadCount(), 1000); // Small delay to ensure backend has processed
    };

    const handleNewMessage = (event) => {
      // Refresh unread count when a new message is received
      fetchUnreadCount();
    };

    window.addEventListener('markAllMessagesAsRead', handleMarkAllAsRead);
    window.addEventListener('chatMarkedAsRead', handleChatMarkedAsRead);
    window.addEventListener('updateUnreadCount', handleUpdateUnreadCount);
    window.addEventListener('messageSent', handleMessageSent);
    window.addEventListener('newMessageReceived', handleNewMessage);
    
    return () => {
      window.removeEventListener('markAllMessagesAsRead', handleMarkAllAsRead);
      window.removeEventListener('chatMarkedAsRead', handleChatMarkedAsRead);
      window.removeEventListener('updateUnreadCount', handleUpdateUnreadCount);
      window.removeEventListener('messageSent', handleMessageSent);
      window.removeEventListener('newMessageReceived', handleNewMessage);
    };
  }, [userId]); // Only depend on user.id, not entire user object

  // Mark a specific chat as read
  const markChatAsRead = useCallback((chatId) => {
    if (!userId) return;
    const now = new Date().toISOString();
    const lastReadKey = `lastRead_${chatId}_${userId}`;
    localStorage.setItem(lastReadKey, now);
    
    // Refresh unread count immediately
    fetchUnreadCount();
  }, [userId, fetchUnreadCount]);

  // Mark chat tab as visited (clear unread count)
  const markChatAsVisited = useCallback(() => {
    if (!userId) return;
    const now = new Date().toISOString();
    const lastChatVisitKey = `lastChatVisit_${userId}`;
    localStorage.setItem(lastChatVisitKey, now);
    setLastChatVisit(now);
    
    // Refresh unread count immediately
    fetchUnreadCount();
  }, [userId, fetchUnreadCount]);

  return { unreadCount, loading, refreshUnreadCount: fetchUnreadCount, markChatAsRead, markChatAsVisited };
};